// app/javascript/.../HistoryPage.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { axiosInstance } from '../../utils/axios';
import { useNavigate } from 'react-router-dom';
import PageHeader from './PageHeader';
import { Calendar, ChevronDown, ChevronRight, Clock, User } from 'lucide-react';
import './medichat_mypage.scss';
import { resolveSessionStatus } from '../../utils/sessionStatus';
import AlertModal from '../common/AlertModal';

const fmtDate = (iso) => {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    const m = d.getMonth() + 1;
    const day = d.getDate();
    return `${m}/${day}`;
  } catch { return '-'; }
};

const fmtDateFull = (iso) => {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    const y = d.getFullYear();
    const m = d.getMonth() + 1;
    const day = d.getDate();
    const weekdays = ['日', '月', '火', '水', '木', '金', '土'];
    const w = weekdays[d.getDay()];
    return `${y}年${m}月${day}日（${w}）`;
  } catch { return '-'; }
};

const fmtTime = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const h = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${h}:${min}`;
  } catch { return ''; }
};

const StatusBadge = ({ session }) => {
  const config = resolveSessionStatus(session);
  const label = session?.history_status_label || config.label;
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      padding: '3px 8px',
      borderRadius: '4px',
      fontSize: '10px',
      fontWeight: 600,
      color: config.color,
      background: config.bg,
      whiteSpace: 'nowrap'
    }}>
      {label}
    </span>
  );
};

const namesFrom = (arr) => Array.isArray(arr) ? arr.map(h => h?.name).filter(Boolean) : [];

const resolveCoverageKind = (session) => {
  if (!session) return null;
  if (session.display_coverage_kind) return session.display_coverage_kind;
  if (session.coverage_kind === 'self_pay') return 'self_pay';
  if (session.entry_coverage_hint === 'self_pay') return 'self_pay';
  if (session.entry_scenario === 'self_pay') return 'self_pay';
  return session.coverage_kind || session.entry_coverage_hint || null;
};

const CoverageBadge = ({ kind }) => {
  if (!kind) return null;
  const isSelfPay = kind === 'self_pay';
  return (
    <span style={{
      display: 'inline-flex',
      padding: '2px 6px',
      borderRadius: '4px',
      fontSize: '10px',
      fontWeight: 600,
      background: isSelfPay ? '#fef2f2' : '#ecfdf5',
      color: isSelfPay ? '#dc2626' : '#059669'
    }}>
      {isSelfPay ? '自費' : '保険'}
    </span>
  );
};

const INITIAL_ALERT_STATE = {
  open: false,
  title: '',
  message: '',
  type: 'alert',
  iconType: undefined,
  confirmText: 'OK',
  cancelText: 'キャンセル',
  onConfirm: null,
};

const HistoryPage = () => {
  const [items, setItems] = useState(null);
  const [openToggles, setOpenToggles] = useState({});
  const [cancellingId, setCancellingId] = useState(null);
  // STG UX 2026-07: window.alert / window.confirm はモバイルで OS 標準ダイアログが
  // 表示され UX が壊れるため、統一の AlertModal に置き換える。
  const [alertModal, setAlertModal] = useState(INITIAL_ALERT_STATE);
  const navigate = useNavigate();

  const closeAlert = () => setAlertModal(INITIAL_ALERT_STATE);

  const reloadSessions = async () => {
    try {
      const { data } = await axiosInstance.get('/api/medichat/sessions', { params: { detailed: 'true' } });
      setItems(data?.sessions || []);
    } catch {
      setItems([]);
    }
  };

  useEffect(() => {
    reloadSessions();
  }, []);

  const executeCancel = async (session) => {
    if (!session?.id) return;
    setCancellingId(session.id);
    try {
      await axiosInstance.post(`/api/medichat/sessions/${session.id}/cancel_reservation`);
      await reloadSessions();
    } catch (e) {
      const message = e?.response?.data?.error || 'キャンセルに失敗しました';
      setAlertModal({
        ...INITIAL_ALERT_STATE,
        open: true,
        title: 'エラー',
        message,
        iconType: 'error',
        confirmText: '閉じる',
      });
    } finally {
      setCancellingId(null);
    }
  };

  const handleCancelReservation = (session, event) => {
    event?.stopPropagation?.();
    if (!session?.id || cancellingId) return;
    setAlertModal({
      ...INITIAL_ALERT_STATE,
      open: true,
      title: '予約をキャンセルしますか？',
      message: '※当日キャンセルの場合はキャンセル料が発生する場合があります。',
      type: 'confirm',
      iconType: 'confirm',
      confirmText: 'キャンセルする',
      cancelText: '閉じる',
      onConfirm: () => executeCancel(session),
    });
  };

  const { reservations, finishedOrLater, cancelled, preReservation } = useMemo(() => {
    const list = Array.isArray(items) ? items : [];

    const sessionDateMs = (s) => {
      const iso = s.scheduled_at || s.created_at;
      const ms = iso ? new Date(iso).getTime() : 0;
      return Number.isFinite(ms) ? ms : 0;
    };

    const sortByDateDesc = (a, b) => sessionDateMs(b) - sessionDateMs(a);

    // 進行中一覧の緊急度（小さいほど上）
    // 1. 手続き待ち（支払い・未収納など）
    // 2. 診察中
    // 3. 診察開始待ち（予約確定済み・問診/証憑待ち含む）
    const ACTIVE_HISTORY_GROUP_PRIORITY = {
      post_consultation_task: 1,
      in_progress: 2,
      reservation: 3,
      document_registration: 3,
      pre_questionnaire: 3,
    };

    const ACTIVE_STATUS_PRIORITY = {
      payment_pending: 1,
      billing_confirmation_pending: 1,
      doctor_finished: 1,
      record_review_pending: 1,
      delivery_pending: 1,
      rx_outside_pending: 1,
      in_progress: 2,
      waiting: 3,
      questionnaire: 4,
      doctor_selection: 4,
    };

    const sortActiveByPriority = (a, b) => {
      const priorityA = (a.history_group && ACTIVE_HISTORY_GROUP_PRIORITY[a.history_group])
        || ACTIVE_STATUS_PRIORITY[a.status]
        || 99;
      const priorityB = (b.history_group && ACTIVE_HISTORY_GROUP_PRIORITY[b.history_group])
        || ACTIVE_STATUS_PRIORITY[b.status]
        || 99;
      if (priorityA !== priorityB) return priorityA - priorityB;
      return sortByDateDesc(a, b);
    };

    const hasHistoryContract = list.some((s) => !!s.history_group);

    const reservations = hasHistoryContract
      ? list
        .filter((s) => ['document_registration', 'pre_questionnaire', 'reservation', 'in_progress', 'post_consultation_task'].includes(s.history_group))
        .sort(sortActiveByPriority)
      : list
        .filter(s =>
          ['waiting', 'in_progress'].includes(s.status) ||
          (!!s.scheduled_at && !['questionnaire','doctor_selection','completed','cancelled','payment_pending','delivery_pending','doctor_finished','record_review_pending','billing_confirmation_pending','rx_outside_pending'].includes(s.status))
        )
        .sort(sortActiveByPriority);

    const finishedOrLater = hasHistoryContract
      ? list
        .filter((s) => s.history_group === 'finished')
        .sort(sortByDateDesc)
      : list
        .filter(s => ['doctor_finished','record_review_pending','billing_confirmation_pending','payment_pending','delivery_pending','completed','rx_outside_pending'].includes(s.status))
        .sort(sortByDateDesc);

    const cancelled = hasHistoryContract
      ? list
        .filter((s) => s.history_group === 'cancelled')
        .sort(sortByDateDesc)
      : list
        .filter(s => s.status === 'cancelled')
        .sort(sortByDateDesc);

    // 予約確定前のセッションは診療履歴ではないため表示しない。
    // 予約フローの再開はチャット側の current session 導線で扱う。
    const preReservation = [];

    return { reservations, finishedOrLater, cancelled, preReservation };
  }, [items]);

  const toggleSection = (key) => {
    setOpenToggles(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const getLabel = (s) => {
    const disease = s.primary_disease_name || namesFrom(s.selected_diseases)[0];
    if (disease) return disease;
    const symptoms = namesFrom(s.selected_symptoms);
    if (symptoms[0]) return symptoms[0];
    return s.entry_specialty_hint || s.entry_department_hint || '症状未選択';
  };

  const ReservationCard = ({ s }) => {
    const dateFull = fmtDateFull(s.scheduled_at || s.created_at);
    const timeStr = fmtTime(s.scheduled_at || s.created_at);
    const consultMode = s.consultation_mode === 'follow_up' ? '再診' : '初診';
    const patientName = s.patient_display_name || '本人';
    const resumeUrl = s.resume_url || `/medichat/chat/${s.id}`;
    const ctaLabel = s.history_cta_label || '診察画面を開く';
    // STG UX 2026-07: 「キャンセル」は予約確定済みセッション（status:waiting）のみ表示。
    // 予約前フロー（doctor_selection / pre_reservation）では「離脱」と「キャンセル料が
    // 発生する予約キャンセル」の意味が混同されるため出さない。
    //
    // NOTE: history_group ∈ {reservation, pre_questionnaire, document_registration} は
    //       backend の history_contract_for 実装上いずれも session.waiting? 分岐内で
    //       生成されるため、status==='waiting' の subset。将来サーバー側で history_group
    //       の意味が拡張された時に誤動作しないよう、シンプルに status ベースで判定する。
    //       より厳密な制御が必要になったら backend 側に cancellable フラグを追加する。
    const canCancel = s.status === 'waiting';

    return (
      <div
        className="mc-reservation-card"
        onClick={() => navigate(resumeUrl)}
      >
        <div className="mc-reservation-card__header">
          <StatusBadge session={s} />
          <span className="mc-reservation-card__mode">{consultMode}</span>
          <CoverageBadge kind={resolveCoverageKind(s)} />
        </div>
        <div className="mc-reservation-card__disease">{getLabel(s)}</div>
        <div className="mc-reservation-card__details">
          <div className="mc-reservation-card__detail-row">
            <Calendar size={14} />
            <span>{dateFull}</span>
            {timeStr && <><Clock size={14} /><span>{timeStr}〜</span></>}
          </div>
          <div className="mc-reservation-card__detail-row">
            <User size={14} />
            <span>担当医: {s.doctor_name || '未定'}</span>
            <span className="mc-reservation-card__patient">受診者: {patientName}</span>
          </div>
        </div>
        {s.history_status_note && (
          <div className="mc-reservation-card__details" style={{ marginTop: '8px', color: '#475569' }}>
            <div className="mc-reservation-card__detail-row">
              <span>{s.history_status_note}</span>
            </div>
          </div>
        )}
        <div className="mc-reservation-card__footer">
          <span className="mc-reservation-card__cta">
            {ctaLabel}
            <ChevronRight size={16} />
          </span>
          {canCancel && (
            <button
              type="button"
              className="mc-reservation-card__cancel-btn"
              onClick={(e) => handleCancelReservation(s, e)}
              disabled={cancellingId === s.id}
            >
              {cancellingId === s.id ? '処理中…' : 'キャンセル'}
            </button>
          )}
        </div>
      </div>
    );
  };

  const HistoryCard = ({ s }) => {
    const isMigrated = s.is_migrated_from_karaken === true;
    const isClickable = !(isMigrated && s.status === 'cancelled');
    const dateStr = fmtDate(s.scheduled_at || s.created_at);
    const timeStr = fmtTime(s.scheduled_at || s.created_at);
    const patientName = s.patient_display_name || '本人';
    const consultMode = s.consultation_mode === 'follow_up' ? '再診' : '初診';
    
    const hasPrescriptions = Array.isArray(s.prescriptions) && s.prescriptions.length > 0;
    const hasExaminations = Array.isArray(s.examinations) && s.examinations.length > 0;
    const filteredDocuments = Array.isArray(s.documents) 
      ? s.documents.filter(d => d.doc_type === 'DOC_CERT')
      : [];
    const hasDocuments = filteredDocuments.length > 0;
    const hasReceipt = s.status === 'completed' && !!s.receipt_url;
    
    return (
      <div 
        className="mc-history-card"
        onClick={() => isClickable && navigate(s.resume_url || `/medichat/chat/${s.id}`)}
        style={{ 
          cursor: isClickable ? 'pointer' : 'default', 
          opacity: (isMigrated && s.status === 'cancelled') ? 0.6 : 1 
        }}
      >
        <div className="mc-history-card__row1">
          <div className="mc-history-card__date">
            <span className="mc-history-card__date-num">{dateStr}</span>
            {timeStr && <span className="mc-history-card__date-time">{timeStr}</span>}
          </div>
          <span className="mc-history-card__label">{getLabel(s)}</span>
          <span className="mc-history-card__mode-badge">{consultMode}</span>
          <StatusBadge session={s} />
        </div>
        
        <div className="mc-history-card__row2">
          <span className="mc-history-card__info-row">
            受診者: {patientName} ・ 担当医: {s.doctor_name || '-'}
          </span>
          <div className="mc-history-card__badges">
            <CoverageBadge kind={resolveCoverageKind(s)} />
            <span className="mc-history-card__session-id">診察ID: {s.id}</span>
          </div>
        </div>
        
        {hasPrescriptions && (
          <div className="mc-history-card__section">
            <div className="mc-history-card__section-title">💊 処方薬</div>
            <ul className="mc-history-card__list">
              {s.prescriptions.map(p => (
                <li key={p.id}>
                  <span className="mc-history-card__drug">{p.drug_name}</span>
                  {(p.instruction_text || p.count_text) && (
                    <span className="mc-history-card__dosage">
                      {[p.instruction_text, p.count_text].filter(Boolean).join(' / ')}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
        
        {!hasPrescriptions && !isMigrated && ['doctor_finished', 'record_review_pending', 'billing_confirmation_pending', 'payment_pending', 'delivery_pending', 'completed', 'rx_outside_pending'].includes(s.status) && (
          <div className="mc-history-card__section">
            <div className="mc-history-card__no-rx">💊 処方なし</div>
          </div>
        )}
        
        {hasExaminations && (
          <div className="mc-history-card__section">
            <div className="mc-history-card__section-title">🧪 検査</div>
            <ul className="mc-history-card__list">
              {s.examinations.map(e => (
                <li key={e.id}>
                  {e.name}{e.quantity > 1 && ` ×${e.quantity}`}
                </li>
              ))}
            </ul>
          </div>
        )}
        
        {s.status === 'completed' && (hasReceipt || hasDocuments) && (
          <div className="mc-history-card__docs">
            {hasReceipt && (
              <a 
                href={s.receipt_url} 
                target="_blank" 
                rel="noreferrer" 
                className="mc-history-card__doc-link"
                onClick={(e) => e.stopPropagation()}
              >
                📋 領収書
              </a>
            )}
            {filteredDocuments.map(d => (
              d.url ? (
                <a 
                  key={d.id}
                  href={d.url} 
                  target="_blank" 
                  rel="noreferrer" 
                  className="mc-history-card__doc-link"
                  onClick={(e) => e.stopPropagation()}
                >
                  📄 {d.name}
                </a>
              ) : (
                <span key={d.id} className="mc-history-card__doc-pending">
                  📄 {d.name}（作成中）
                </span>
              )
            ))}
          </div>
        )}
      </div>
    );
  };

  const SectionTitle = ({ children }) => (
    <h3 className="mc-history-section-title">{children}</h3>
  );

  const ToggleSection = ({ sectionKey, title, count, children }) => {
    const isOpen = !!openToggles[sectionKey];
    return (
      <div className="mc-history-section">
        <button
          type="button"
          className="mc-history-toggle"
          onClick={() => toggleSection(sectionKey)}
        >
          <span className="mc-history-toggle__title">{title}</span>
          <span className="mc-history-toggle__count">{count}件</span>
          {isOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
        </button>
        {isOpen && <div className="mc-history-toggle__body">{children}</div>}
      </div>
    );
  };

  return (
    <div className="mc-history-page">
      <PageHeader title="診療履歴" />
      
      <div className="mc-history-content">
        {!items ? (
          <div className="mc-history-loading">
            <div className="mc-history-loading__spinner" />
            <span>読み込み中...</span>
          </div>
        ) : items.length === 0 ? (
          <div className="mc-history-empty">
            <Calendar size={48} />
            <p>診療履歴はありません</p>
          </div>
        ) : (
          <>
            {/* 予約中の診察 - 常に表示、大きなカード */}
            <div className="mc-history-section">
              <SectionTitle>進行中の診察・手続き</SectionTitle>
              {reservations.length > 0 ? (
                reservations.map(s => <ReservationCard key={s.id} s={s} />)
              ) : (
                <div className="mc-history-empty-inline">
                  <Calendar size={32} color="#94a3b8" />
                  <p>現在予約している診察はありません</p>
                </div>
              )}
            </div>

            {/* 受診済み - 現状のまま */}
            {finishedOrLater.length > 0 && (
              <div className="mc-history-section">
                <SectionTitle>受診済み</SectionTitle>
                {finishedOrLater.map(s => <HistoryCard key={s.id} s={s} />)}
              </div>
            )}

            {/* キャンセル済み - トグル */}
            {cancelled.length > 0 && (
              <ToggleSection sectionKey="cancelled" title="キャンセル済み" count={cancelled.length}>
                {cancelled.map(s => <HistoryCard key={s.id} s={s} />)}
              </ToggleSection>
            )}

            {/* 受診前 - トグル */}
            {preReservation.length > 0 && (
              <ToggleSection sectionKey="preReservation" title="受診前" count={preReservation.length}>
                {preReservation.map(s => <HistoryCard key={s.id} s={s} />)}
              </ToggleSection>
            )}
          </>
        )}
      </div>

      <AlertModal
        open={alertModal.open}
        title={alertModal.title}
        message={alertModal.message}
        type={alertModal.type}
        iconType={alertModal.iconType}
        confirmText={alertModal.confirmText}
        cancelText={alertModal.cancelText}
        onClose={closeAlert}
        onConfirm={alertModal.onConfirm || undefined}
      />
    </div>
  );
};

export default HistoryPage;
